import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, AlertCircle, ArrowLeft, Download, Share2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

import { API_URL } from '../utils/apiConfig';

export default function Receipt() {
  const { receiptId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReceipt();
  }, [receiptId]);

  const fetchReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/${receiptId}`);
      
      if (response.ok) {
        const data = await response.json();
        setReceipt(data);
      } else {
        setError('Receipt not found');
      }
    } catch (err) {
      setError('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-8 h-8 text-emerald-600" />;
      case 'pending':
        return <Clock className="w-8 h-8 text-amber-600" />;
      default:
        return <AlertCircle className="w-8 h-8 text-red-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-red-100 text-red-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Receipt Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="receipt-page">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-lg overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                <span className="text-emerald-700 font-bold text-lg">P</span>
              </div>
              <span className="font-bold text-xl text-white">PeCash</span>
            </div>
            <p className="text-emerald-100 text-sm">Transaction Receipt</p>
          </div>

          {/* Status */}
          <div className="p-6 text-center border-b border-slate-100">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              {getStatusIcon(receipt.status)}
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Payment Successful</h2>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(receipt.status)}`}>
              {receipt.status.toUpperCase()}
            </span>
          </div>

          {/* Amount */}
          <div className="p-6 text-center bg-slate-50">
            <p className="text-sm text-slate-500 mb-1">Amount</p>
            <p className="text-4xl font-bold text-slate-900">${receipt.amount.toFixed(2)}</p>
            <p className="text-sm text-slate-500">{receipt.currency}</p>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-500">Receipt ID</span>
              <span className="font-mono text-slate-900 text-sm" data-testid="receipt-id">{receipt.receipt_id}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-500">Transaction ID</span>
              <span className="font-mono text-slate-900 text-sm">{receipt.transaction_id.slice(0, 12)}...</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-500">Date & Time</span>
              <span className="text-slate-900 text-sm">{receipt.timestamp_formatted}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-500">Beneficiary</span>
              <span className="text-slate-900">{receipt.recipient_masked}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-500">Fee</span>
              <span className="text-slate-900">${receipt.fee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-slate-500 font-medium">Total</span>
              <span className="text-slate-900 font-bold">${receipt.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 bg-slate-50 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.print()}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Receipt link copied!');
              }}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Footer */}
          <div className="p-4 text-center border-t border-slate-100">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} PeCashGlobal. All rights reserved.
            </p>
            <Link to="/" className="text-xs text-emerald-600 hover:underline mt-1 inline-block">
              www.pecashglobal.com
            </Link>
          </div>
        </motion.div>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-slate-500 hover:text-emerald-600 text-sm">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Back to PeCash
          </Link>
        </div>
      </div>
    </div>
  );
}
